// Location: assets/bal-scent-slider.js
// What: Controls scent carousel navigation, loop snapping, and focus behavior.
// Why: Keeps the carousel directly draggable while retaining caption navigation.
// RELEVANT FILES: sections/bal-scent-slider.liquid, assets/bal-scent-slider.css
(() => {
  const initialize = (root) => {
    if (root.dataset.scReady) return;
    root.dataset.scReady = 'true';
    const track = root.querySelector('[data-track]');
    const inner = root.querySelector('[data-inner]');
    const all = [...track.querySelectorAll('.sc__item')];
    const N = Number(root.dataset.count) || 1;
    const loop = root.dataset.loop === 'true' && all.length === N * 3;
    const base = loop ? N : 0;
    const speed = Number(root.dataset.speed) || 700;
    const caps = [...root.querySelectorAll('.sc__cap')];
    const dots = [...root.querySelectorAll('[data-goto]')];
    if (!all.length) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let v = base + (Number(root.dataset.start) || 0);
    let timer = null, snapTimer = null, moving = false, drag = null;
    let visible = !('IntersectionObserver' in window), imagesStarted = false;

    const mod = (n) => ((n % N) + N) % N;
    const centerTo = (el) => { track.style.transform = `translateX(${inner.clientWidth / 2 - (el.offsetLeft + el.offsetWidth / 2)}px)`; };
    const still = (fn) => {
      track.classList.add('no-anim');
      fn();
      void track.offsetWidth;                      /* force layout with transition disabled */
      requestAnimationFrame(() => requestAnimationFrame(() => track.classList.remove('no-anim')));
    };
    const paint = () => {
      const logical = mod(v - base);
      all.forEach((el, k) => {
        el.classList.toggle('is-active', k === v);
        el.classList.toggle('is-motif-active', mod(k - base) === logical);
      });
      caps.forEach((el, k) => { el.classList.toggle('is-active', k === logical); el.inert = k !== logical; el.setAttribute('aria-hidden', k !== logical); });
      dots.forEach((el, k) => el.setAttribute('aria-selected', k === logical));
      centerTo(all[v]);
    };
    /* After a slide ends on a clone, jump (invisibly) to the identical original so the next move is always forward. */
    const snap = () => {
      clearTimeout(snapTimer); moving = false;
      if (!loop || (v >= base && v < base + N)) return;
      v = base + mod(v - base);
      still(() => { all.forEach((el, k) => el.classList.toggle('is-active', k === v)); centerTo(all[v]); });
    };
    const go = (delta) => {
      if (moving) snap();                           /* settle any in-flight move first */
      if (loop) v += delta; else v = Math.min(all.length - 1, Math.max(0, v + delta));
      moving = true;
      paint();
      clearTimeout(snapTimer); snapTimer = setTimeout(snap, speed + 60);   /* fallback if transitionend never fires */
    };
    const goTo = (logical) => {
      const cur = mod(v - base); let d = logical - cur;
      if (loop && Math.abs(d) > N / 2) d += d > 0 ? -N : N;
      if (d !== 0) go(d);
    };
    track.addEventListener('transitionend', (e) => { if (e.target === track && moving) snap(); });

    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      stop();
      if (!visible || !all[v].classList.contains('is-motif-ready') || drag || root.matches(':focus-within') || root.matches(':hover') || document.hidden || N < 2 || root.dataset.autoplay !== 'true' || reduce || (window.Shopify && Shopify.designMode)) return;
      timer = setInterval(() => go(1), Number(root.dataset.interval) || 6000);
    };

    /* Start every copy of a motif together only after its pixels are available. */
    const prepareMotifs = () => {
      if (imagesStarted) return;
      imagesStarted = true;
      for (let logical = 0; logical < N; logical++) {
        const copies = all.filter((el, k) => mod(k - base) === logical);
        const images = copies.flatMap(el => [...el.querySelectorAll('.sc__motif img')]);
        Promise.all(images.map(img => new Promise(resolve => {
          const ready = () => {
            if (img.decode && img.naturalWidth) img.decode().catch(() => {}).then(resolve);
            else resolve();
          };
          if (img.complete) ready();
          else { img.addEventListener('load', ready, { once: true }); img.addEventListener('error', ready, { once: true }); }
          img.loading = 'eager';
        }))).then(() => requestAnimationFrame(() => {
          copies.forEach(el => el.classList.add('is-motif-ready'));
          if (mod(v - base) === logical) start();
        }));
      }
    };

    root.querySelector('[data-prev]')?.addEventListener('click', () => { go(-1); start(); });
    root.querySelector('[data-next]')?.addEventListener('click', () => { go(1); start(); });
    dots.forEach(d => d.addEventListener('click', () => { goTo(Number(d.dataset.goto)); start(); }));
    all.forEach((el, k) => el.addEventListener('click', e => { if (e.target.closest('a')) return; if (k !== v) go(k - v); start(); }));
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', () => setTimeout(start, 0));
    root.addEventListener('mouseenter', stop); root.addEventListener('mouseleave', start);
    root.addEventListener('keydown', e => { if (e.target.closest('a')) return; if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1); });
    let suppressClickUntil = 0;
    const stage = root.querySelector('.sc__stage');
    const finishDrag = (e, cancelled = false) => {
      if (!drag || e.pointerId !== drag.id) return;
      const current = drag; drag = null;
      stage.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      if (root.hasPointerCapture(current.id)) root.releasePointerCapture(current.id);
      if (current.axis === 'x') {
        suppressClickUntil = Date.now() + 700;
        const threshold = Math.min(60, all[v].offsetWidth * .2);
        go(!cancelled && Math.abs(current.dx) >= threshold ? (current.dx < 0 ? 1 : -1) : 0);
      } else go(0);
      start();
    };
    root.addEventListener('pointerdown', e => {
      if (drag || e.isPrimary === false || e.button !== 0 || N < 2 || e.target.closest('button, input, select, textarea')) return;
      stop();
      /* Freeze the rendered position, not the destination of an interrupted transition. */
      const matrix = getComputedStyle(track).transform.match(/^matrix(3d)?\((.+)\)$/);
      let origin = matrix ? Number(matrix[2].split(',')[matrix[1] ? 12 : 4]) : inner.clientWidth / 2 - (all[v].offsetLeft + all[v].offsetWidth / 2);
      clearTimeout(snapTimer); moving = false;
      if (loop && (v < base || v >= base + N)) {
        const normalized = base + mod(v - base);
        origin += all[v].offsetLeft - all[normalized].offsetLeft;
        v = normalized;
        all.forEach((el, k) => el.classList.toggle('is-active', k === v));
      }
      track.classList.add('is-dragging');
      track.style.transform = `translateX(${origin}px)`;
      void track.offsetWidth;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, axis: null, origin };
    });
    root.addEventListener('pointermove', e => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.axis && Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
      if (!drag.axis) drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (drag.axis !== 'x') return;
      if (!root.hasPointerCapture(e.pointerId)) root.setPointerCapture(e.pointerId);
      e.preventDefault();
      stage.classList.add('is-dragging'); track.classList.add('is-dragging');
      drag.dx = dx;
      let distance = Math.max(-all[v].offsetWidth, Math.min(all[v].offsetWidth, dx));
      if (!loop && ((v === 0 && dx > 0) || (v === all.length - 1 && dx < 0))) distance *= .25;
      track.style.transform = `translateX(${drag.origin + distance}px)`;
    });
    root.addEventListener('pointerup', e => finishDrag(e));
    root.addEventListener('pointercancel', e => finishDrag(e, true));
    /* Release gestures outside the section before horizontal capture begins. */
    addEventListener('pointerup', e => finishDrag(e));
    addEventListener('pointercancel', e => finishDrag(e, true));
    root.addEventListener('lostpointercapture', e => { if (e.target === root) finishDrag(e, true); });
    let wheelTotal = 0, lastWheel = 0, wheelLocked = false;
    root.addEventListener('wheel', e => {
      if (drag || N < 2 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const now = Date.now();
      if (now - lastWheel > 180) { wheelTotal = 0; wheelLocked = false; }
      lastWheel = now;
      if (wheelLocked) return;
      wheelTotal += e.deltaX * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? inner.clientWidth : 1);
      if (Math.abs(wheelTotal) < 35) return;
      stop(); go(wheelTotal > 0 ? 1 : -1); start();
      wheelLocked = true;
    }, { passive: false });
    root.addEventListener('dragstart' , e => e.preventDefault());
    root.addEventListener('click', e => {
      if (Date.now() < suppressClickUntil) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    addEventListener('resize', () => { still(() => centerTo(all[v])); });
    root.querySelectorAll('img').forEach(img => { if (!img.complete) img.addEventListener('load', () => { if (!moving && !drag) still(() => centerTo(all[v])); }); });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    document.addEventListener('shopify:block:select', e => { const d = e.target.closest('[data-goto]'); if (!d || !root.contains(d)) return; goTo(Number(d.dataset.goto)); stop(); });

    still(paint);
    const onVisible = (shown) => {
      visible = shown;
      if (!visible) { stop(); return; }
      root.classList.add('sc-revealed');
      prepareMotifs();
      start();
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => onVisible(entries[0].isIntersecting), { threshold: .1 });
      observer.observe(stage);
      document.addEventListener('shopify:section:unload', e => {
        if (!e.target.contains(root)) return;
        observer.disconnect(); stop(); clearTimeout(snapTimer);
      });
    } else onVisible(true);
  };
  document.querySelectorAll('[data-sc]').forEach(initialize);
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-sc]').forEach(initialize));
})();
